import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import Button from '../components/Button'
import Cover from '../components/Cover'
import CtaBand from '../components/CtaBand'
import PageHero from '../components/PageHero'
import { Section } from '../components/Section'
import NotFound from './NotFound'
import { getPost, getPostNeighbours } from '../articles'
import { formatDate } from '../lib/format'
import { articleJsonLd, useJsonLd, useSeo } from '../lib/seo'

export default function Post() {
  const { slug } = useParams()
  const post = getPost(slug)

  if (!post) return <NotFound />

  return <Article post={post} />
}

/**
 * Split out so the hooks below only ever run with a real post - calling
 * `useSeo` conditionally would break the rules of hooks.
 *
 * The article's text is its own component (src/articles/<slug>.jsx); everything
 * around it is here.
 */
function Article({ post }) {
  const { previous, next } = getPostNeighbours(post.slug)
  const Body = post.Body

  useSeo({
    title: post.title,
    description: post.excerpt,
    path: `/insights/${post.slug}`,
    type: 'article',
    breadcrumbLabel: post.title,
    article: {
      published: post.date,
      // The articles record no revision date, so the published date is reused
      // rather than stamped with "now" - a modified time that moves on every
      // deploy tells Google the article changed when it did not.
      modified: post.date,
      section: post.category,
    },
  })

  /*
   * `useMemo` because `useJsonLd` compares its argument by reference: an object
   * literal would be a new value on every render, tearing the <script> down and
   * re-adding it each time.
   */
  useJsonLd(useMemo(() => articleJsonLd(post), [post]), `post-${post.slug}`)

  return (
    <>
      <PageHero
        eyebrow={post.category}
        title={post.title}
        breadcrumb={[{ label: 'Insights', to: '/insights' }, { label: post.title }]}
      >
        <p className="article__meta" style={{ marginTop: 0 }}>
          <time dateTime={post.date}>{formatDate(post.date)}</time>
          <span aria-hidden="true" />
          {post.minutes} Min. Lesezeit
        </p>
      </PageHero>

      <Section className="article__body">
        <div className="article__cover">
          <Cover seed={post.slug} label={post.category} />
        </div>

        <article className="prose prose--lead">
          <Body />
        </article>

        <div className="article__footer">
          <p style={{ margin: 0, fontSize: 'var(--step--1)' }}>
            Fragen zu diesem Thema? Wir beantworten sie gern.
          </p>
          <Button to="/kontakt" variant="outline" icon="arrowRight">
            Kontakt aufnehmen
          </Button>
        </div>

        {previous || next ? (
          <nav className="article-nav" aria-label="Weitere Beiträge">
            {previous ? (
              <Link to={`/insights/${previous.slug}`} className="article-nav__item">
                <span className="article-nav__dir">Neuerer Beitrag</span>
                <span className="article-nav__title">{previous.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to={`/insights/${next.slug}`}
                className="article-nav__item article-nav__item--next"
              >
                <span className="article-nav__dir">Älterer Beitrag</span>
                <span className="article-nav__title">{next.title}</span>
              </Link>
            ) : null}
          </nav>
        ) : null}
      </Section>

      <CtaBand />
    </>
  )
}
